/*
 * Column presets for the Sites table: one job per view instead of twenty
 * columns at once. Pure (no React) so it is unit-tested in siteColumns.test.js.
 *
 * Every column keeps its fixed width: the table uses table-layout: fixed,
 * where an unsized column collapses to zero, so its min-width is the sum of
 * what is showing.
 */

/* Table order. `wideOnly` columns are dropped on phones, as before. */
export const COLUMNS = [
  { key: 'dns', width: 180, wideOnly: true },
  { key: 'hosting', width: 180, wideOnly: true },
  { key: 'hits', width: 100 },
  { key: 'human', width: 100 },
  { key: 'readRate', width: 80 },
  { key: 'readTime', width: 80 },
  { key: 'adsense', width: 100 },
  { key: 'amazon', width: 100 },
  { key: 'total', width: 110 },
  { key: 'impr', width: 100 },
  { key: 'adsPerHit', width: 86 },
  { key: 'ecpm', width: 86 },
  { key: 'rpm', width: 86 },
  { key: 'amzImpr', width: 90 },
  { key: 'amzClicks', width: 90 },
  { key: 'trend', width: 140, wideOnly: true },
  { key: 'pages', width: 76, wideOnly: true },
  { key: 'newMo', width: 86, wideOnly: true },
  { key: 'scheduled', width: 96 },
  { key: 'attention', width: 210, wideOnly: true },
];

const ALL_KEYS = COLUMNS.map((c) => c.key);

export const VIEWS = [
  { key: 'revenue', label: 'Revenue', hint: 'Views, earnings by source and RPM',
    columns: ['hits', 'human', 'adsense', 'amazon', 'total', 'rpm', 'trend'] },
  { key: 'traffic', label: 'Traffic & ads', hint: 'Reading, ad delivery and Amazon offers',
    columns: ['hits', 'human', 'readRate', 'readTime', 'impr', 'adsPerHit', 'ecpm', 'rpm', 'amzImpr', 'amzClicks', 'trend'] },
  { key: 'content', label: 'Content', hint: 'Pages, publishing and what needs attention',
    columns: ['hits', 'human', 'pages', 'newMo', 'scheduled', 'attention'] },
  { key: 'infra', label: 'Infrastructure', hint: 'DNS target and hosting service',
    columns: ['dns', 'hosting', 'hits', 'pages'] },
  { key: 'all', label: 'All', hint: 'Every column', columns: ALL_KEYS },
];

export const DEFAULT_VIEW = 'revenue';

export const viewOf = (key) => VIEWS.find((v) => v.key === key) || VIEWS.find((v) => v.key === DEFAULT_VIEW);

/* { key: true } for the columns this view shows at this width. */
export const visibleColumns = (viewKey, { wide = true } = {}) => {
  const want = new Set(viewOf(viewKey).columns);
  const out = {};
  COLUMNS.forEach((c) => {
    if (want.has(c.key) && (wide || !c.wideOnly)) out[c.key] = true;
  });
  return out;
};

/* The site column's width: narrower when the two infrastructure columns show. */
export const siteColumnWidth = (show) => (show.dns || show.hosting ? 260 : 320);

/* Fixed-layout min-width: the favourite and site columns plus what is showing. */
export const tableMinWidth = (show) =>
  44 + siteColumnWidth(show) + COLUMNS.reduce((acc, c) => acc + (show[c.key] ? c.width : 0), 0);

/* How many visible columns sit before / after the three money columns, for
   the earnings rows' colSpans; null when the view does not show all three. */
export const moneySpans = (show) => {
  if (!(show.adsense && show.amazon && show.total)) return null;
  const idx = COLUMNS.findIndex((c) => c.key === 'adsense');
  const before = 2 + COLUMNS.slice(0, idx).filter((c) => show[c.key]).length;
  const after = COLUMNS.slice(idx + 3).filter((c) => show[c.key]).length;
  return { before, after };
};
